import { useEffect, useState } from 'react';
import {
  Paper, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Stack, Alert, Box, Snackbar,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import DownloadIcon from '@mui/icons-material/Download';
import { useParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { officerNav } from '../components/officerNav';
import api from '../services/api';

export default function BidOpening() {
  const { tenderId } = useParams();
  const [data, setData] = useState(null);
  const [toast, setToast] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const res = await api.get(`/tenders/${tenderId}/bids`);
    setData(res.data.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tenderId]);

  const openBids = async () => {
    if (!window.confirm('Open all bids for this tender? This is recorded and cannot be undone.')) return;
    setBusy(true);
    try {
      await api.post(`/tenders/${tenderId}/bids/open`);
      setToast('Bids opened');
      load();
    } catch (err) {
      setToast(err.response?.data?.error?.message || 'Could not open bids');
    } finally {
      setBusy(false);
    }
  };

  const download = async (docId, fileName) => {
    try {
      const res = await api.get(`/bid-documents/${docId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = fileName; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setToast(err.response?.data?.error?.message || 'Download not permitted');
    }
  };

  const tender = data?.tender;
  const opened = data?.opened;

  return (
    <DashboardLayout nav={officerNav} title="Bid Opening">
      {tender && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6">{tender.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            Scheduled opening: {tender.bidOpeningDate ? new Date(tender.bidOpeningDate).toLocaleString('en-GB') : '—'}
          </Typography>
          {opened
            ? <Alert severity="success" sx={{ mt: 2 }}>Bids were opened on {new Date(tender.bidsOpenedAt).toLocaleString('en-GB')}.</Alert>
            : <Stack direction="row" sx={{ mt: 2 }}><Button variant="contained" onClick={openBids} disabled={busy}>Open Bids</Button></Stack>}
        </Paper>
      )}

      <Typography variant="h6" sx={{ mb: 2 }}>Bids received ({data?.bids?.length || 0})</Typography>
      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Bid Ref</TableCell><TableCell>Vendor</TableCell><TableCell>Submitted</TableCell>
              <TableCell>Status</TableCell><TableCell>Documents</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.bids || []).map((b) => (
              <TableRow key={b.id}>
                <TableCell>{b.reference}</TableCell>
                <TableCell>{b.vendorName}</TableCell>
                <TableCell>{new Date(b.submittedAt).toLocaleString('en-GB')}</TableCell>
                <TableCell><Chip size="small" label={b.status} /></TableCell>
                <TableCell>
                  {!opened && <Typography variant="caption" color="text.secondary">Sealed until opening</Typography>}
                  {opened && b.documents.map((d) => (
                    <Box key={d.id} sx={{ mb: 0.5 }}>
                      {d.available
                        ? <Button size="small" startIcon={<DownloadIcon />} onClick={() => download(d.id, d.fileName)}>{d.docType}</Button>
                        : <Chip size="small" icon={<LockIcon />} label={`${d.docType} (sealed until evaluation)`} variant="outlined" />}
                    </Box>
                  ))}
                </TableCell>
              </TableRow>
            ))}
            {data?.bids?.length === 0 && (
              <TableRow><TableCell colSpan={5}><Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No bids received for this tender.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')}>
        <Alert severity="info" onClose={() => setToast('')}>{toast}</Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
